import { type FC } from "react";

import { clsx } from "clsx";

import { type IconProp } from "@fortawesome/fontawesome-svg-core";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";

import { useFileDropZone } from "./use_file_drop_zone";

/**
 * `.prior-drop` per design handoff Part 2.
 *
 * Amber-themed dashed drop zone for the C1 / PL-4 follow-up screens. Used 2x
 * inside the Records Status Bar body — once for the prior input (original
 * medical records or prior review document) and once for new medical records.
 *
 * Indigo override (`tone="indigo"`) is used inside the focused
 * AddRecordsClassifyOverlay step 2: same shape, indigo-500 dashed border,
 * indigo-50 bg, indigo subtitle/formats text instead of amber.
 */
export const PriorDrop: FC<{
  icon: IconProp;
  title: string;
  subtitle: string;
  formats: string;
  accept: string;
  tone?: "amber" | "indigo";
  multiple?: boolean;
}> = ({
  icon,
  title,
  subtitle,
  formats,
  accept,
  tone = "amber",
  multiple = false,
}) => {
  const { dragging, inputRef, containerProps, inputProps } = useFileDropZone({
    multiple,
  });

  return (
    <div
      {...containerProps}
      className={clsx(
        "flex cursor-pointer items-center gap-3.5 rounded-lg border-2 border-dashed px-[18px] py-5 transition-colors",
        tone === "amber" && "border-amber-400 bg-white/40",
        tone === "indigo" && "border-indigo-500 bg-indigo-50",
        dragging && tone === "amber" && "bg-amber-50",
        dragging && tone === "indigo" && "bg-indigo-100",
      )}
    >
      <input {...inputProps} ref={inputRef} accept={accept} />
      <span
        aria-hidden="true"
        className={clsx(
          "shrink-0 text-2xl leading-none",
          tone === "amber" ? "text-amber-500" : "text-indigo-500",
        )}
      >
        <FontAwesomeIcon icon={icon} />
      </span>
      <div className="min-w-0 flex-1">
        <div className="text-sm font-semibold text-slate-900">{title}</div>
        <div
          className={clsx(
            "text-xs",
            tone === "amber" ? "text-amber-800" : "text-indigo-700",
          )}
        >
          {subtitle}
        </div>
        <div
          className={clsx(
            "mt-px text-[11px]",
            tone === "amber" ? "text-amber-700" : "text-indigo-600",
          )}
        >
          {formats}
        </div>
      </div>
    </div>
  );
};
